import { useState, useEffect, useCallback } from 'react'

import SessionsApiService from '@/apis/sessionsApiService'
import type { User } from '@/types/user'

interface AuthState {
  user: User | null
  isLoading: boolean
}

// Get initial user from window.appConfig if available
function getInitialUser(): User | null {
  const config = (window as unknown as { appConfig?: { user?: User } }).appConfig
  return config?.user || null
}

export default function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: getInitialUser(),
    isLoading: !getInitialUser(),
  })

  // Fetch current user on mount if not already loaded
  useEffect(() => {
    if (state.user) return

    const fetchUser = async () => {
      try {
        const data = await SessionsApiService.show()
        setState({ user: data.user, isLoading: false })
      } catch {
        setState({ user: null, isLoading: false })
      }
    }

    fetchUser()
  }, [state.user])

  const login = useCallback(async (email: string, password: string) => {
    const data = await SessionsApiService.create(email, password)
    setState({ user: data.user, isLoading: false })
    return data.user
  }, [])

  const logout = useCallback(async () => {
    try {
      await SessionsApiService.destroy()
    } finally {
      setState({ user: null, isLoading: false })
      window.location.href = '/login'
    }
  }, [])

  return {
    ...state,
    login,
    logout,
  }
}
